import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  Save,
  AlertCircle,
  CheckCircle2,
  Edit
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from 'react-hot-toast';

interface Interview {
  id: string;
  expert_name: string;
  transcript_text?: string;
}

interface TranscriptEditorModalProps {
  interview: Interview | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function TranscriptEditorModal({
  interview,
  open,
  onClose,
  onSuccess
}: TranscriptEditorModalProps) {
  const [editedTranscript, setEditedTranscript] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (interview && open) {
      setEditedTranscript(interview.transcript_text || '');
      setHasUnsavedChanges(false);
      setError(null);
    }
  }, [interview, open]);

  const handleTranscriptChange = (value: string) => {
    setEditedTranscript(value);
    setHasUnsavedChanges(value !== (interview?.transcript_text || ''));
  };

  const handleSave = async () => {
    if (!interview) return;

    try {
      setSaving(true);
      setError(null);

      const response = await authFetch(
        `/api/interviews/${interview.id}/transcript`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transcript: editedTranscript })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save transcript');
      }

      toast.success('Transcript saved successfully');
      setHasUnsavedChanges(false);
      onSuccess();

    } catch (err) {
      console.error('[TranscriptEditor] Error saving transcript:', err);
      const message = err instanceof Error ? err.message : 'Failed to save transcript';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmed = confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
  };

  if (!interview) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Transcript - {interview.expert_name}
          </DialogTitle>
          <DialogDescription>
            Review and correct transcription errors. Changes will be saved to the interview record.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!interview.transcript_text ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This interview does not have a transcript yet. Complete the interview recording first.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Transcript</label>
                {hasUnsavedChanges && (
                  <span className="text-xs text-orange-600 font-semibold flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Unsaved changes
                  </span>
                )}
              </div>
              <Textarea
                value={editedTranscript}
                onChange={(e) => handleTranscriptChange(e.target.value)}
                className="min-h-[400px] font-mono text-sm"
                placeholder="Transcript text..."
                disabled={saving}
              />
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>{editedTranscript.length} characters</span>
                <span>{Math.ceil(editedTranscript.length / 5)} words (approx)</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            {hasUnsavedChanges ? 'Cancel' : 'Close'}
          </Button>

          {interview.transcript_text && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setEditedTranscript(interview.transcript_text || '');
                  setHasUnsavedChanges(false);
                }}
                disabled={!hasUnsavedChanges || saving}
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Transcript
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
